import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
	AlertTriangle,
	ArrowRight,
	RefreshCw,
	Eye,
	CheckCircle2,
	ExternalLink,
	HelpCircle,
	Pencil,
	Search,
	Sparkles,
	Trash2,
	Loader2,
} from "lucide-react";
import type { PageIssue, ProposedFix } from "@/types/technicalAudit";
import { technicalAuditService } from "@/services/technicalAuditService";
import { sanitizeBrokenUrl } from "./issueBeforeAfter";

interface ResolverSuggestionCardProps {
	issue: PageIssue;
	/**
	 * Called when the SEO specialist edits the fix destination, with the full
	 * updated suggestion (cause/action preserved, new destination URL applied).
	 * The drawer stores it as `editedValue` and sends it to the backend as
	 * `approvedValue` on Apply.
	 */
	onEdit?: (value: ProposedFix) => void;
}

/**
 * Renders + lets the specialist EDIT the suggestion produced by the backend
 * "resolver" path for broken_internal / links_to_redirect / indexability /
 * sitemap_error issues.
 *
 * Stored as a JSON string in `issue.proposed_value` / `approved_value`. Shapes:
 *   { action: "redirect",         replacement_url, ... }          (dead-page 301)
 *   { indexability_cause/sitemap_cause: "dead_*", replacement_url } (seeded)
 *   { action: "repoint_links",    link_edits[] }                  (links_to_redirect)
 *   { action: "resubmit_recheck", reasoning }                     (read-only)
 *   { action: "review",           reasoning }                     (read-only)
 */
interface LinkEdit {
	from_url: string;
	to_url: string;
	status_code?: number;
}

interface ResolverSuggestion {
	action?: string;
	replacement_url?: string | null;
	confidence?: string;
	match_score?: number;
	reasoning?: string;
	indexability_cause?: string;
	sitemap_cause?: string;
	dead_url?: string;
	candidates?: Array<{ url: string; title?: string; match_score?: number }>;
	link_edits?: LinkEdit[];
	checked_links?: number;
	// broken_external resolver payload
	generation_source?: string;
	broken_target_url?: string;
	source_url?: string;
	link_text?: string | null;
	status_code?: number;
	verified_status_code?: number;
	// 403/429/5xx come back as review (likely a bot/WAF block, not a dead link).
	auto_remove_safe?: boolean;
	// links_to_broken resolver payload — one repoint/remove edit per broken
	// internal target found on the source page.
	source_page_url?: string;
	broken_count?: number;
	repoint_count?: number;
	remove_count?: number;
	edits?: LinksToBrokenEdit[];
}

interface LinksToBrokenEdit {
	target_url: string;
	anchor?: string;
	status_code?: number | null;
	confidence?: string;
	match_score?: number;
	action?: "repoint" | "remove" | string;
	replacement_url?: string | null;
}

const BROKEN_EXTERNAL_SOURCE = "technical_audit_broken_external_resolver";
const LINKS_TO_BROKEN_SOURCE = "technical_audit_links_to_broken_resolver";

function parseSuggestion(issue: PageIssue): ResolverSuggestion | null {
	const raw =
		(issue.approved_value as unknown) ?? (issue.proposed_value as unknown);
	if (!raw) return null;
	if (typeof raw === "object") return raw as ResolverSuggestion;
	if (typeof raw === "string") {
		try {
			return JSON.parse(raw) as ResolverSuggestion;
		} catch {
			return null;
		}
	}
	return null;
}

// A pending broken_external issue has NO stored suggestion — its fix isn't produced
// by /generate-suggestion (the backend rejects that type). Synthesize a default
// "remove" suggestion from the crawl details so the editor (replacement URL /
// candidates / remove framing) renders instead of the "Click Generate suggestion"
// dead-end. parseSuggestion still wins whenever a real value exists.
function defaultBrokenExternalSuggestion(
	issue: PageIssue,
): ResolverSuggestion | null {
	if (issue.issue_type !== "broken_external") return null;
	const d = (issue.issue_details ?? {}) as {
		target_url?: string;
		page_url?: string;
		link_text?: string | null;
		status_code?: number;
	};
	if (!d.target_url) return null;
	return {
		generation_source: BROKEN_EXTERNAL_SOURCE,
		action: "remove",
		broken_target_url: d.target_url,
		source_url: d.page_url ?? issue.page_url ?? undefined,
		link_text: d.link_text ?? null,
		status_code: d.status_code,
	};
}

// redirect / redirect_chain have a DETERMINISTIC fix target — the chain's final
// 200 URL — so they are NOT produced by /generate-suggestion (the backend rejects
// "redirect"). Synthesize a "collapse to the final URL" suggestion from the crawl
// details so the editor pre-fills the destination instead of dead-ending on the
// "Click Generate suggestion" prompt. parseSuggestion still wins whenever a real
// stored value exists.
function defaultRedirectSuggestion(issue: PageIssue): ResolverSuggestion | null {
	if (issue.issue_type !== "redirect" && issue.issue_type !== "redirect_chain")
		return null;
	const d = (issue.issue_details ?? {}) as {
		final_url?: string;
		redirect_chain?: string[];
	};
	const finalUrl =
		d.final_url ||
		(Array.isArray(d.redirect_chain) && d.redirect_chain.length > 0
			? d.redirect_chain[d.redirect_chain.length - 1]
			: undefined);
	if (!finalUrl) return null;
	return {
		generation_source: "technical_audit_redirect_collapse",
		action: "redirect",
		replacement_url: finalUrl,
	};
}

const URL_INPUT_CLS =
	"w-full rounded-md border border-border bg-card px-3 py-2 font-mono text-sm text-foreground focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal";

export function ResolverSuggestionCard({
	issue,
	onEdit,
}: ResolverSuggestionCardProps) {
	const s =
		parseSuggestion(issue) ??
		defaultBrokenExternalSuggestion(issue) ??
		defaultRedirectSuggestion(issue);
	const draftKey = `${issue.id}:${JSON.stringify(issue.approved_value ?? issue.proposed_value ?? "")}`;
	const initialDraft = {
		destUrl: typeof s?.replacement_url === "string" ? s.replacement_url : "",
		linkUrls: Array.isArray(s?.link_edits)
			? s.link_edits.map((e) => e.to_url)
			: Array.isArray(s?.edits)
				? s.edits.map((e) => e.replacement_url ?? "")
				: [],
	};
	const [draft, setDraft] = useState<{
		key: string;
		destUrl: string;
		linkUrls: string[];
	} | null>(null);
	// Optional new anchor text (relabel), applied with repoint/remove.
	const [anchorText, setAnchorText] = useState("");
	const destUrl = draft?.key === draftKey ? draft.destUrl : initialDraft.destUrl;
	const linkUrls = draft?.key === draftKey ? draft.linkUrls : initialDraft.linkUrls;

	// Review-only replacement candidates (Wayback + web search) for broken_external.
	const [candidates, setCandidates] = useState<
		{ url: string; source: string; title: string }[] | null
	>(null);
	const [loadingCandidates, setLoadingCandidates] = useState(false);
	const [candidatesError, setCandidatesError] = useState<string | null>(null);
	// The original "broken" target actually resolved on a live re-check — likely
	// a bot/WAF false positive rather than a dead link.
	const [originalReachable, setOriginalReachable] = useState(false);
	// LLM rewrite of the copy that holds the dead link. `rewriteScope` sets the
	// blast radius (drop-link → sentence → paragraph → whole section).
	type RewriteScope = "remove_link" | "sentence" | "paragraph" | "section";
	const [rewriteScope, setRewriteScope] = useState<RewriteScope>("sentence");
	const [rewriteLoading, setRewriteLoading] = useState(false);
	const [rewrite, setRewrite] = useState<{
		scope: RewriteScope;
		before_html: string;
		after_html: string;
	} | null>(null);
	const [rewriteError, setRewriteError] = useState<string | null>(null);
	// True once the reviewer has staged a generated rewrite as the fix to apply.
	const [rewriteSelected, setRewriteSelected] = useState(false);

	const fetchRewrite = async (scope: RewriteScope) => {
		setRewriteScope(scope);
		setRewriteLoading(true);
		setRewriteError(null);
		setRewrite(null);
		setRewriteSelected(false);
		try {
			const res = await technicalAuditService.rewriteBrokenExternalSection(
				issue.id,
				scope,
			);
			if (!res.enabled) {
				setRewriteError("Section rewrite is turned off.");
			} else if (res.error || !res.after_html) {
				setRewriteError(res.error || "Couldn't generate a rewrite.");
			} else {
				setRewrite({
					scope,
					before_html: res.before_html || "",
					after_html: res.after_html,
				});
			}
		} catch {
			setRewriteError("Couldn't generate a rewrite — try again.");
		} finally {
			setRewriteLoading(false);
		}
	};

	const applyRewrite = () => {
		if (!rewrite) return;
		onEdit?.({
			...s,
			action: "rewrite_section",
			scope: rewrite.scope,
			section_before: rewrite.before_html,
			section_after: rewrite.after_html,
		} as unknown as ProposedFix);
		setRewriteSelected(true);
	};

	const fetchCandidates = async () => {
		setLoadingCandidates(true);
		setCandidatesError(null);
		try {
			const res =
				await technicalAuditService.suggestBrokenExternalReplacements(issue.id);
			setCandidates(res.candidates || []);
			setOriginalReachable(Boolean(res.original_reachable));
		} catch {
			setCandidatesError("Couldn't load suggestions — paste a URL above or remove the link.");
		} finally {
			setLoadingCandidates(false);
		}
	};

	// Auto-load replacement candidates when a broken_external card opens, so a
	// working-link option is offered up front (not buried behind a click).
	useEffect(() => {
		if (issue.issue_type !== "broken_external") return;
		setCandidates(null);
		setCandidatesError(null);
		setOriginalReachable(false);
		setRewrite(null);
		setRewriteError(null);
		setRewriteSelected(false);
		void fetchCandidates();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [issue.id, issue.issue_type]);

	if (!s) {
		return (
			<Card className="border-border shadow-sm">
				<CardContent className="py-6 text-center text-sm text-muted-foreground">
					Click <span className="font-medium">Generate suggestion</span> to
					analyze this issue and propose a fix.
				</CardContent>
			</Card>
		);
	}

	const isRepointLinks =
		Array.isArray(s.link_edits) && s.link_edits.length > 0;
	// links_to_broken: batched repoint/remove edits (one per broken internal
	// target). Distinct from links_to_redirect's `link_edits` shape.
	const isLinksToBroken =
		s.generation_source === LINKS_TO_BROKEN_SOURCE ||
		s.action === "repoint_or_remove_broken_links" ||
		(Array.isArray(s.edits) && s.edits.length > 0);
	const isResubmit = s.action === "resubmit_recheck";
	// Dead-page causes stay editable (specialist can add a target even if the
	// resolver found none); only genuinely-protected causes are review-only.
	const isBrokenExternal =
		s.generation_source === BROKEN_EXTERNAL_SOURCE ||
		(!isRepointLinks &&
			!isResubmit &&
			(s.action === "remove" || s.action === "repoint") &&
			(typeof s.broken_target_url === "string" ||
				typeof s.source_url === "string"));

	// broken_external review items render their own card (which has a repoint
	// input + remove framing), not the generic read-only review card.
	const isReviewOnly =
		s.action === "review" &&
		!isBrokenExternal &&
		s.indexability_cause !== "dead_page" &&
		s.sitemap_cause !== "dead_in_sitemap";

	// --- emit the edited suggestion (preserve cause/action, swap destination) ---
	const emitReplacement = (url: string) => {
		setDraft({ key: draftKey, destUrl: url, linkUrls });
		onEdit?.({ ...s, replacement_url: url } as unknown as ProposedFix);
	};
	// broken_external: empty replacement => remove link (keep anchor); a URL =>
	// repoint the link to that destination.
	const emitBrokenExternal = (
		url: string,
		text: string = anchorText,
		action?: "delete",
	) => {
		const trimmedUrl = url.trim();
		const trimmedText = text.trim();
		setDraft({ key: draftKey, destUrl: url, linkUrls });
		if (action === "delete") {
			onEdit?.({
				...s,
				action: "delete",
				replacement_url: null,
				new_text: null,
			} as unknown as ProposedFix);
			return;
		}
		onEdit?.({
			...s,
			action: trimmedUrl ? "repoint" : "remove",
			replacement_url: trimmedUrl || null,
			new_text: trimmedText || null,
		} as unknown as ProposedFix);
	};
	const emitLink = (idx: number, url: string) => {
		const next = linkUrls.slice();
		next[idx] = url;
		setDraft({ key: draftKey, destUrl, linkUrls: next });
		const edits = (s.link_edits ?? []).map((e, i) => ({
			...e,
			to_url: next[i] ?? e.to_url,
		}));
		onEdit?.({ ...s, link_edits: edits } as unknown as ProposedFix);
	};
	// links_to_broken: per broken internal target, a filled URL => repoint to it;
	// blank => remove the link (keep the anchor text). Recomputes the whole edits
	// array so repoint_count/remove_count stay consistent for the backend apply.
	const emitBrokenEdit = (idx: number, url: string) => {
		const next = linkUrls.slice();
		next[idx] = url;
		setDraft({ key: draftKey, destUrl, linkUrls: next });
		const edits = (s.edits ?? []).map((e, i) => {
			const replacement = (next[i] ?? "").trim();
			return {
				...e,
				replacement_url: replacement || null,
				action: replacement ? "repoint" : "remove",
			};
		});
		const repoint_count = edits.filter((e) => e.action === "repoint").length;
		onEdit?.({
			...s,
			edits,
			repoint_count,
			remove_count: edits.length - repoint_count,
		} as unknown as ProposedFix);
	};

	// ---- Repoint redirecting links (links_to_redirect) — editable to_url ----
	if (isRepointLinks) {
		return (
			<Card className="border-green-200 dark:border-green-800 shadow-sm bg-green-50 dark:bg-green-950/40/30">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-green-900 dark:text-green-300 flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
							<CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
						</div>
						Repoint {(s.link_edits ?? []).length} redirecting link
						{(s.link_edits ?? []).length === 1 ? "" : "s"}
						<Badge
							variant="outline"
							className="ml-auto bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800"
						>
							editable
						</Badge>
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-3">
					{s.reasoning && (
						<div className="p-3 bg-card rounded-lg border border-green-200 dark:border-green-800">
							<p className="text-sm text-green-800 dark:text-green-300">{s.reasoning}</p>
						</div>
					)}
					<ul className="space-y-3">
						{(s.link_edits ?? []).map((edit, idx) => (
							<li
								key={`${edit.from_url}:${idx}`}
								className="rounded-md border border-green-200 dark:border-green-800 bg-card px-3 py-2 space-y-1.5"
							>
								<div className="font-mono text-xs text-muted-foreground break-all">
									{edit.from_url}
									{edit.status_code ? (
										<span className="ml-2 text-amber-600 dark:text-amber-400">
											({edit.status_code})
										</span>
									) : null}
								</div>
								<label className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-green-700 dark:text-green-400">
									<ArrowRight className="w-3 h-3" /> repoint to
								</label>
								<input
									type="url"
									className={URL_INPUT_CLS}
									value={linkUrls[idx] ?? edit.to_url}
									onChange={(e) => emitLink(idx, e.target.value)}
									placeholder="https://example.com/final-page/"
								/>
							</li>
						))}
					</ul>
				</CardContent>
			</Card>
		);
	}

	// ---- Repoint or remove broken internal links (links_to_broken) ----
	//      One editable row per broken target: a URL repoints, blank removes.
	if (isLinksToBroken) {
		const edits = s.edits ?? [];
		return (
			<Card className="border-green-200 dark:border-green-800 shadow-sm bg-green-50 dark:bg-green-950/40/30">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-green-900 dark:text-green-300 flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
							<CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
						</div>
						Repoint or remove {edits.length} broken link
						{edits.length === 1 ? "" : "s"}
						<Badge
							variant="outline"
							className="ml-auto bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800"
						>
							editable
						</Badge>
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-3">
					{s.reasoning && (
						<div className="p-3 bg-card rounded-lg border border-green-200 dark:border-green-800">
							<p className="text-sm text-green-800 dark:text-green-300">{s.reasoning}</p>
						</div>
					)}
					<ul className="space-y-3">
						{edits.map((edit, idx) => {
							const replacement = linkUrls[idx] ?? edit.replacement_url ?? "";
							const willRepoint = replacement.trim().length > 0;
							return (
								<li
									key={`${edit.target_url}:${idx}`}
									className="rounded-md border border-green-200 dark:border-green-800 bg-card px-3 py-2 space-y-1.5"
								>
									<div className="flex items-start justify-between gap-2">
										<div className="font-mono text-xs text-muted-foreground break-all flex-1">
											{sanitizeBrokenUrl(edit.target_url)}
										</div>
										{typeof edit.status_code === "number" ? (
											<span className="text-amber-600 dark:text-amber-400 text-xs whitespace-nowrap">
												({edit.status_code})
											</span>
										) : null}
									</div>
									{edit.anchor ? (
										<div className="text-xs text-muted-foreground">
											Anchor text: &ldquo;{edit.anchor}&rdquo;
										</div>
									) : null}
									<label className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-green-700 dark:text-green-400">
										<ArrowRight className="w-3 h-3" />
										{willRepoint
											? "repoint to"
											: "replacement URL (blank = remove link, keep anchor)"}
									</label>
									<input
										type="url"
										className={URL_INPUT_CLS}
										value={replacement}
										onChange={(e) => emitBrokenEdit(idx, e.target.value)}
										placeholder="https://example.com/live-page/ (blank to remove)"
									/>
								</li>
							);
						})}
					</ul>
				</CardContent>
			</Card>
		);
	}

	// ---- Resubmit / recheck (redirect-in-sitemap) — read-only ----
	if (isResubmit) {
		return (
			<Card className="border-sky-200 dark:border-sky-800 shadow-sm bg-sky-50 dark:bg-sky-950/40/40">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-sky-950 flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-900/40 flex items-center justify-center">
							<RefreshCw className="w-4 h-4 text-sky-700 dark:text-sky-400" />
						</div>
						Resubmit &amp; re-check
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<div className="p-3 bg-card rounded-lg border border-sky-200 dark:border-sky-800">
						<p className="text-sm text-sky-900 dark:text-sky-300">{s.reasoning}</p>
					</div>
				</CardContent>
			</Card>
		);
	}

	// ---- Review only (protected noindex / canonical) — read-only ----
	if (isReviewOnly) {
		return (
			<Card className="border-amber-200 dark:border-amber-800 shadow-sm bg-amber-50 dark:bg-amber-950/40/40">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-amber-900 dark:text-amber-300 flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
							{s.reasoning ? (
								<Eye className="w-4 h-4 text-amber-700 dark:text-amber-400" />
							) : (
								<HelpCircle className="w-4 h-4 text-amber-700 dark:text-amber-400" />
							)}
						</div>
						Review only — not auto-applied
						{(s.indexability_cause || s.sitemap_cause) && (
							<Badge
								variant="outline"
								className="ml-auto bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 border-amber-200 dark:border-amber-800"
							>
								{s.indexability_cause || s.sitemap_cause}
							</Badge>
						)}
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0">
					<div className="p-3 bg-card rounded-lg border border-amber-200 dark:border-amber-800">
						<p className="text-sm text-amber-900 dark:text-amber-300">
							{s.reasoning ||
								"This issue needs manual review; no automatic fix is applied."}
						</p>
					</div>
				</CardContent>
			</Card>
		);
	}

	// ---- Broken external link — recommend "remove link (keep anchor text)";
	//      optionally supply a replacement URL to repoint instead. ----
	if (isBrokenExternal) {
		const willRepoint = destUrl.trim().length > 0;
		// 403/429/5xx come back as a review action: likely a bot/WAF block, not a
		// dead link — don't nudge removal, but still allow a repoint override.
		const isReview = s.action === "review" || s.auto_remove_safe === false;
		const brokenUrl = s.broken_target_url || s.dead_url || "";
		const sourcePage = s.source_url || issue.page_url;
		return (
			<Card className="border-teal/40 shadow-sm bg-teal-light">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-teal-dark flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-teal/20 flex items-center justify-center">
							<CheckCircle2 className="w-4 h-4 text-teal-dark" />
						</div>
						{willRepoint
							? "Repoint broken external link"
							: isReview
								? "Review — verify before removing"
								: "Remove link (keep anchor text)"}
						<Badge
							variant="outline"
							className="ml-auto bg-teal/15 text-teal-dark border-teal/30"
						>
							{s.confidence ? `${s.confidence} · editable` : "editable"}
						</Badge>
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-3">
					<div className="space-y-1">
						<div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
							Broken link
							{typeof s.verified_status_code === "number"
								? ` (${s.verified_status_code})`
								: typeof s.status_code === "number"
									? ` (${s.status_code})`
									: ""}
						</div>
						<div className="font-mono text-xs text-muted-foreground break-all">
							{sanitizeBrokenUrl(brokenUrl)}
						</div>
						{s.link_text ? (
							<div className="text-xs text-muted-foreground">
								Anchor text: &ldquo;{s.link_text}&rdquo;
							</div>
						) : null}
						{sourcePage ? (
							<div className="font-mono text-[11px] text-muted-foreground break-all">
								on {sourcePage}
							</div>
						) : null}
					</div>
					{originalReachable && (
						<div className="rounded-lg border border-coral/40 bg-coral-light p-3">
							<p className="flex items-start gap-2 text-sm text-coral-dark">
								<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
								<span>
									<span className="font-semibold">Likely a false positive.</span>{" "}
									This link actually loads for us right now (HTTP 200) — the
									crawler&apos;s error looks like a bot/WAF block, not a dead
									link. Consider keeping it as-is; only replace it if you&apos;ve
									confirmed in a browser that it&apos;s genuinely gone.
								</span>
							</p>
						</div>
					)}
					<div className="rounded-lg border border-teal/30 bg-card p-3">
						<p className="text-sm text-teal-dark">
							{willRepoint
								? "This link will be repointed to the replacement URL below; the anchor text is kept."
								: isReview
									? "Our crawler's error is often a bot/WAF block, not a dead link — the page may work for real visitors. Verify it in a browser; only remove if it is genuinely gone, or add a replacement URL below to repoint."
									: "Recommended: remove the dead link and keep the anchor text as plain text. To send the link somewhere live instead, add a replacement URL below."}
						</p>
					</div>
					<div>
						<label className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-teal-dark mb-1">
							<Pencil className="w-3 h-3" /> replacement URL (optional —
							leave blank to remove)
						</label>
						<input
							type="url"
							className={URL_INPUT_CLS}
							value={destUrl}
							onChange={(e) => emitBrokenExternal(e.target.value)}
							placeholder="https://example.com/working-page/"
						/>
						<label className="mt-3 flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-teal-dark mb-1">
							<Pencil className="w-3 h-3" /> anchor text (optional — relabel the
							link text)
						</label>
						<input
							type="text"
							className={URL_INPUT_CLS}
							value={anchorText}
							onChange={(e) => {
								setAnchorText(e.target.value);
								emitBrokenExternal(destUrl, e.target.value);
							}}
							placeholder={
								s.link_text ? `currently: ${s.link_text}` : "New anchor text"
							}
						/>
						<button
							type="button"
							onClick={() => emitBrokenExternal("", "", "delete")}
							title="Remove the link and its anchor text entirely"
							className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-destructive/40 px-2.5 py-1 text-xs font-medium text-destructive hover:bg-destructive/5"
						>
							<Trash2 className="w-3.5 h-3.5" /> Delete link + text
						</button>
						<div className="mt-2">
							<button
								type="button"
								onClick={fetchCandidates}
								disabled={loadingCandidates}
								className="inline-flex items-center gap-1.5 rounded-md border border-teal/40 px-2.5 py-1 text-xs font-medium text-teal-dark hover:bg-teal-light disabled:opacity-50"
							>
								{loadingCandidates ? (
									<Loader2 className="w-3.5 h-3.5 animate-spin" />
								) : (
									<Search className="w-3.5 h-3.5" />
								)}
								{loadingCandidates
									? "Finding working replacements…"
									: candidates
										? "Search again"
										: "Suggest replacements"}
							</button>
							{candidatesError && (
								<p className="mt-1 text-xs text-destructive">{candidatesError}</p>
							)}
							{candidates && candidates.length === 0 && !loadingCandidates && (
								<p className="mt-1 text-xs text-muted-foreground">
									No replacement found — paste a URL above, or leave blank to remove.
								</p>
							)}
							{candidates && candidates.length > 0 && (
								<>
								<ul className="mt-2 space-y-1">
									{candidates.map((c) => (
										<li key={c.url} className="flex items-stretch gap-1">
											<button
												type="button"
												onClick={() => emitBrokenExternal(c.url)}
												title="Use this URL as the replacement"
												className="min-w-0 flex-1 rounded-md border border-teal/30 bg-card p-2 text-left hover:bg-teal-light"
											>
												<span className="flex items-center gap-2">
													<Badge
														variant="outline"
														className="bg-teal/15 text-teal-dark border-teal/30 text-[10px]"
													>
														{c.source === "wayback" ? "archive" : "web"}
													</Badge>
													<span className="truncate text-xs font-medium text-foreground">
														{c.title}
													</span>
												</span>
												<span className="mt-0.5 block break-all font-mono text-[11px] text-muted-foreground">
													{c.url}
												</span>
											</button>
											<a
												href={c.url}
												target="_blank"
												rel="noopener noreferrer"
												onClick={(e) => e.stopPropagation()}
												title="Open in a new tab to check it actually loads"
												className="flex shrink-0 items-center gap-1 rounded-md border border-teal/30 bg-card px-2 text-[11px] font-medium text-teal-dark hover:bg-teal-light"
											>
												<ExternalLink className="h-3.5 w-3.5" />
												check
											</a>
										</li>
									))}
								</ul>
								<p className="mt-1.5 text-[11px] text-muted-foreground">
									Open “check” to confirm a page actually loads before applying — automated checks can miss soft-404s (a “not found” page that still returns HTTP 200).
								</p>
								</>
							)}
						</div>
							<div className="mt-3 border-t border-teal/20 pt-3">
								<p className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-coral-dark mb-1.5">
									<Sparkles className="w-3 h-3" /> Rewrite with AI
								</p>
								<div className="flex items-center gap-2">
									<select
										value={rewriteScope}
										onChange={(e) => setRewriteScope(e.target.value as RewriteScope)}
										disabled={rewriteLoading}
										className="rounded-md border border-border bg-card px-2 py-1 text-xs text-foreground focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal disabled:opacity-50"
									>
										<option value="remove_link">Remove link only</option>
										<option value="sentence">Rewrite sentence</option>
										<option value="paragraph">Rewrite paragraph</option>
										<option value="section">Rewrite section</option>
									</select>
									<button
										type="button"
										onClick={() => fetchRewrite(rewriteScope)}
										disabled={rewriteLoading}
										className="inline-flex items-center gap-1.5 rounded-md border border-coral/40 px-2.5 py-1 text-xs font-medium text-coral-dark hover:bg-coral-light disabled:opacity-50"
									>
										{rewriteLoading ? (
											<Loader2 className="w-3.5 h-3.5 animate-spin" />
										) : (
											<Sparkles className="w-3.5 h-3.5" />
										)}
										{rewriteLoading ? "Writing…" : "Generate"}
									</button>
								</div>
								<p className="mt-1.5 text-[11px] text-muted-foreground">
									Pick how much to rewrite — from just dropping the link to reworking the whole section. You’ll review the before/after before anything is applied.
								</p>
								{rewriteError && (
									<p className="mt-1 text-xs text-destructive">{rewriteError}</p>
								)}
								{rewrite && (
									<div className="mt-2 space-y-2">
										<div>
											<p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
												Before
											</p>
											<div
												className="rounded-md border border-border bg-muted p-2 text-xs text-muted-foreground [&_a]:text-coral [&_a]:underline"
												dangerouslySetInnerHTML={{ __html: rewrite.before_html }}
											/>
										</div>
										<div>
											<p className="text-[10px] font-semibold uppercase tracking-wide text-teal-dark">
												After
											</p>
											<div
												className="rounded-md border border-teal/30 bg-teal-light p-2 text-xs text-foreground [&_a]:text-coral [&_a]:underline"
												dangerouslySetInnerHTML={{ __html: rewrite.after_html }}
											/>
										</div>
										{rewriteSelected ? (
											<div className="flex items-start gap-1.5 rounded-md border border-teal/40 bg-teal-light px-2.5 py-1.5 text-xs font-medium text-teal-dark">
												<CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
												<span>
													This rewrite is selected. Click{" "}
													<span className="font-semibold">“Apply to WordPress”</span>{" "}
													below to publish it.
												</span>
											</div>
										) : (
											<button
												type="button"
												onClick={applyRewrite}
												className="inline-flex items-center gap-1.5 rounded-md bg-coral px-2.5 py-1 text-xs font-medium text-white hover:bg-coral-hover"
											>
												<CheckCircle2 className="w-3.5 h-3.5" /> Use this rewrite
											</button>
										)}
									</div>
								)}
							</div>
					</div>
					{s.reasoning && (
						<div className="p-3 bg-card rounded-lg border border-teal/30">
							<p className="text-sm text-teal-dark">{s.reasoning}</p>
						</div>
					)}
				</CardContent>
			</Card>
		);
	}

	// ---- Redirect / replacement (dead page, indexability/sitemap dead,
	//      broken_internal) — EDITABLE destination URL ----
	const candidateOptions = (s.candidates ?? []).filter(
		(c) => c.url && c.url !== destUrl,
	);
	return (
		<Card className="border-green-200 dark:border-green-800 shadow-sm bg-green-50 dark:bg-green-950/40/30">
			<CardHeader className="pb-3">
				<CardTitle className="text-sm font-semibold text-green-900 dark:text-green-300 flex items-center gap-2">
					<div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/40 flex items-center justify-center">
						<CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
					</div>
					Redirect destination
					<Badge
						variant="outline"
						className="ml-auto bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800"
					>
						{s.confidence ? `${s.confidence} · editable` : "editable"}
					</Badge>
				</CardTitle>
			</CardHeader>
			<CardContent className="pt-0 space-y-3">
				<div className="font-mono text-xs text-muted-foreground break-all">
					{s.dead_url || issue.page_url}
				</div>
				<div>
					<label className="flex items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-green-700 dark:text-green-400 mb-1">
						<Pencil className="w-3 h-3" /> redirect to (editable)
					</label>
					<input
						type="url"
						className={URL_INPUT_CLS}
						value={destUrl}
						onChange={(e) => emitReplacement(e.target.value)}
						placeholder="https://example.com/destination/"
					/>
				</div>
				{s.reasoning && (
					<div className="p-3 bg-card rounded-lg border border-green-200 dark:border-green-800">
						<p className="text-sm text-green-800 dark:text-green-300">{s.reasoning}</p>
					</div>
				)}
				{candidateOptions.length > 0 && (
					<div className="space-y-1.5">
						<div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
							Suggested targets (click to use)
						</div>
						{candidateOptions.map((c) => (
							<button
								type="button"
								key={c.url}
								onClick={() => emitReplacement(c.url)}
								className="block w-full text-left text-xs text-muted-foreground font-mono break-all rounded px-2 py-1 hover:bg-green-100"
							>
								{c.url}
								{typeof c.match_score === "number"
									? ` (score ${c.match_score})`
									: ""}
							</button>
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
}
